/**
 * LinkedIn Stüdyosu — /linkedin-studyo. İç ekip (@glowytics.com / @heyglowy.com).
 * Konu fikri → gönderi metni → görsel sahnesi → Gemini görseli → yazılı kart.
 * Veri /api/linkedin-studyo'dan gelir (backend alan adı kilitli, buradaki kozmetik).
 */
import React, { useEffect, useState } from 'react';
import {
  Lightbulb, PenLine, Image as ImageIcon, Sparkles, Copy, Check, Download, RefreshCw,
  Save, Trash2, AlertTriangle, Wand2, LayoutTemplate, FolderOpen, Plus, Upload, X, BookOpen,
  Newspaper, Package, CalendarDays, ExternalLink, ChevronDown, ChevronUp, Compass,
} from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';

const API = 'https://api.glowytics.com/api/linkedin-studyo';

const T = {
  kart: '#FFFFFF', cizgi: '#E5E7EB', cizgi2: '#F3F4F6', yuzey: '#F8FAFF',
  metin: '#111827', metin2: '#374151', metin3: '#6B7280', mavi: '#436AEB', maviAcik: '#EEF2FF',
  kirmizi: '#DC2626', turuncu: '#D97706', yesil: '#059669',
};

// Türler ekibin kendi gönderilerinden (Tarzımız sekmesindeki örnekler). Her tür kendi
// görsel dilini de getiriyor; ipucu metni o gönderide ne yapıldığını hatırlatıyor.
const TURLER = [
  { k: 'haber', ad: 'Haber analizi', stil: 'glowy3d', ipucu: 'YouTube gönderisi gibi: Glowy 3D görsel, sol üstte platformun resmî logosu, altta başlık.' },
  { k: 'lansman', ad: 'Ürün lansmanı', stil: 'glowy3d', ipucu: 'Glowy Studio gönderisi gibi: en iyisi gerçek ürün ekran görüntüleriyle önce/sonra şeması. Glowy 3D yedek seçenek.' },
  { k: 'etkinlik', ad: 'Etkinlik', stil: 'illustrasyon', ipucu: 'Web Summit gönderisi gibi: şehrin/mekânın illüstrasyonu, başlık yok, sol üstte glowytics | etkinlik logosu.', baslikYok: true },
  { k: 'kutlama', ad: 'Kutlama', stil: 'editoryal', ipucu: 'High5 gönderisi gibi: Gemini görseli yerine etkinliğin gerçek fotoğrafını kullan.' },
  { k: 'gorus', ad: 'Görüş / nasıl yapılır', stil: 'glowy3d', ipucu: 'Glowy 3D görsel + kısa başlık.' },
];
const TUR_ADI = Object.fromEntries(TURLER.map((t) => [t.k, t.ad]));
const UZUNLUKLAR = [['otomatik', 'Türe göre'], ['kisa', 'Kısa'], ['orta', 'Orta'], ['uzun', 'Uzun']];
const STILLER = [['glowy3d', 'Glowy 3D'], ['illustrasyon', 'İllüstrasyon'], ['editoryal', 'Editoryal fotoğraf'], ['serbest', 'Serbest']];
const ORANLAR = ['4:5', '1:1', '16:9'];
const DURUMLAR = [['taslak', 'Taslak'], ['hazir', 'Hazır'], ['yayinlandi', 'Yayınlandı']];
const HIZLI_DUZELTME = ['Daha kısa yap', 'Girişi daha çarpıcı yap', 'Daha samimi yaz', 'Daha teknik yaz', 'Sonuna net bir soru ekle', 'Emojileri kaldır'];
const LINKEDIN_KESIM = 200;

// "Ne paylaşalım" önerilerinin kaynak türleri ve öncelik rozetleri.
const ONERI_KAYNAK = {
  haber: { ad: 'Gündem', ikon: Newspaper, renk: '#0E7490', zemin: '#ECFEFF' },
  ozellik: { ad: 'Özelliklerimiz', ikon: Package, renk: '#7C3AED', zemin: '#F5F3FF' },
  takvim: { ad: 'Takvim', ikon: CalendarDays, renk: '#B45309', zemin: '#FFFBEB' },
};
const ONCELIK = {
  bugun: { ad: 'Bugün', renk: '#fff', zemin: '#DC2626' },
  'bu-hafta': { ad: 'Bu hafta', renk: '#fff', zemin: '#436AEB' },
  planla: { ad: 'Planla', renk: '#374151', zemin: '#F3F4F6' },
};

async function cagir(yol, govde, yontem = 'POST') {
  const res = await apiFetch(`${API}${yol}`, {
    method: yontem,
    headers: { 'Content-Type': 'application/json' },
    body: govde ? JSON.stringify(govde) : undefined,
  });
  const veri = await res.json().catch(() => ({}));
  // Uzun uçlar (görsel) nabız yüzünden hatayı da 200 gövdesinde döndürüyor.
  if (!res.ok || veri.error) throw new Error(veri.error || `HTTP ${res.status}`);
  return veri;
}

const tarihSaat = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Istanbul' }) : '');

async function indir(url, ad) {
  try {
    const r = await fetch(url);
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = ad;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  } catch (_) {
    window.open(url, '_blank', 'noopener');
  }
}

/* ───────────────────────────── küçük parçalar ───────────────────────────── */

function Cip({ aktif, onClick, children, kucuk }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: kucuk ? '4px 10px' : '6px 12px', borderRadius: 999, fontSize: kucuk ? 12 : 13, fontWeight: 500, cursor: 'pointer',
      border: `1px solid ${aktif ? T.mavi : T.cizgi}`, background: aktif ? T.mavi : T.kart, color: aktif ? '#fff' : T.metin2,
    }}>{children}</button>
  );
}

function Dugme({ onClick, disabled, children, ikincil, tehlike, title }) {
  const renk = tehlike ? T.kirmizi : T.mavi;
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
      border: `1px solid ${ikincil ? T.cizgi : renk}`, background: ikincil ? T.kart : renk, color: ikincil ? (tehlike ? T.kirmizi : T.metin2) : '#fff',
    }}>{children}</button>
  );
}

function Sutun({ no, ikon: Ikon, baslik, sag, children }) {
  return (
    <section style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, padding: 18, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ width: 24, height: 24, borderRadius: 999, background: T.maviAcik, color: T.mavi, fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{no}</span>
        <Ikon size={16} color={T.metin2} />
        <div style={{ fontSize: 15, fontWeight: 600, color: T.metin }}>{baslik}</div>
        {sag && <div style={{ marginLeft: 'auto' }}>{sag}</div>}
      </div>
      {children}
    </section>
  );
}

const Etiket = ({ children }) => (
  <div style={{ fontSize: 12, fontWeight: 600, color: T.metin3, margin: '12px 0 6px' }}>{children}</div>
);

const alan = {
  width: '100%', border: `1px solid ${T.cizgi}`, borderRadius: 8, padding: '8px 10px', fontSize: 13,
  color: T.metin, background: T.kart, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical',
};

function Hata({ mesaj }) {
  if (!mesaj) return null;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#FEF2F2', border: '1px solid #FECACA', color: T.kirmizi, borderRadius: 8, padding: '8px 10px', fontSize: 12, marginTop: 10 }}>
      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> <span>{mesaj}</span>
    </div>
  );
}

function Yukleniyor({ metin }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.metin3, marginTop: 10 }}>
      <RefreshCw size={13} style={{ animation: 'lsDon 1s linear infinite' }} /> {metin}
    </div>
  );
}

/* ───────────────────────────── sayfa ───────────────────────────── */

const BOS = {
  id: null, konu: '', aci: '', not: '', kaynak: '', metin: '', kancalar: [], etiketler: [], notlar: [],
  hero: '', alt: '', sahneler: [], istem: '', gorseller: [], secili: null, kartlar: [], durum: 'taslak', tur: 'gorus', oneriRef: '',
};

// Konu logosu dosyadan data URL'e; backend yalnız resim data URL'i kabul ediyor.
const dosyadanDataUrl = (f) => new Promise((ok, red) => {
  if (f.size > 500 * 1024) { red(new Error('Logo en fazla 500 KB olabilir')); return; }
  const r = new FileReader();
  r.onload = () => ok(r.result);
  r.onerror = () => red(new Error('Dosya okunamadı'));
  r.readAsDataURL(f);
});

export default function LinkedInStudyo() {
  const [sekme, setSekme] = useState('yeni');
  const [hesap, setHesap] = useState('glowytics');

  // 1 · konu
  const [tema, setTema] = useState('');
  const [fikirler, setFikirler] = useState([]);
  const [uzunluk, setUzunluk] = useState('otomatik');
  const [kaynakAcik, setKaynakAcik] = useState(false);

  // gönderi (kaydedilen her şey burada)
  const [g, setG] = useState(BOS);
  const guncelle = (p) => setG((o) => ({ ...o, ...p }));
  const turSec = (k) => {
    const t = TURLER.find((x) => x.k === k) || TURLER[4];
    guncelle({ tur: t.k });
    if (t.k === 'haber') setKaynakAcik(true);
    setStil(t.stil);
    setBaslikYok(!!t.baslikYok);
  };
  const aktifTur = TURLER.find((x) => x.k === g.tur) || TURLER[4];

  // 3 · görsel
  const [stil, setStil] = useState('glowy3d');
  const [enBoy, setEnBoy] = useState('4:5');
  const [yonlendirme, setYonlendirme] = useState('');
  const [logo, setLogo] = useState('glowytics');
  const [talimat, setTalimat] = useState('');
  const [konuLogo, setKonuLogo] = useState('');
  const [baslikYok, setBaslikYok] = useState(false);

  const [mesgul, setMesgul] = useState({});
  const [hata, setHata] = useState({});
  const [kopyalandi, setKopyalandi] = useState(false);
  const [kayitDurum, setKayitDurum] = useState('');
  const [kartUyari, setKartUyari] = useState('');

  const [kayitlar, setKayitlar] = useState([]);
  const [kaynakUyari, setKaynakUyari] = useState('');

  const calistir = async (anahtar, fn) => {
    setMesgul((m) => ({ ...m, [anahtar]: true }));
    setHata((h) => ({ ...h, [anahtar]: '' }));
    try { await fn(); } catch (e) { setHata((h) => ({ ...h, [anahtar]: e.message })); }
    setMesgul((m) => ({ ...m, [anahtar]: false }));
  };

  const gecmisiYukle = () => calistir('gecmis', async () => {
    const v = await cagir('/gecmis', null, 'GET');
    setKayitlar(v.kayitlar || []);
  });

  useEffect(() => { if (sekme === 'taslaklar') gecmisiYukle(); }, [sekme]); // eslint-disable-line react-hooks/exhaustive-deps

  /* --- 1 · fikir --- */
  const fikirUret = () => calistir('fikir', async () => {
    const v = await cagir('/fikir', { hesap, tema, adet: 6 });
    setFikirler(v.fikirler || []);
  });

  // Öneriden yazmaya: konu/açı/tür dolar. Haberse duyurunun metni Kaynak alanına çekilir
  // (haber türü kaynaksız yazılmıyor); çekilemezse RSS özeti + link konur, ekip tamamlar.
  const onerideYaz = async (o) => {
    const k = o.kaynak;
    setG({
      ...BOS,
      konu: o.konu,
      aci: [o.aci, o.kanca ? `Kanca önerisi: ${o.kanca}` : ''].filter(Boolean).join('\n'),
      not: k.tip === 'ozellik' ? `Özellik: ${k.baslik}. ${k.ozet}` : k.tip === 'takvim' ? `Tarih: ${k.baslik}${k.ozet ? `. ${k.ozet}` : ''}` : '',
      oneriRef: k.tip === 'haber' ? `haber:${k.link}` : `${k.tip === 'ozellik' ? 'O' : 'T'}:${o.ref.slice(2)}`,
      tur: o.tur,
    });
    setFikirler([]); setKayitDurum(''); setKartUyari(''); setKaynakUyari('');
    turSec(o.tur);
    if (k.tip !== 'haber') return;
    setKaynakAcik(true);
    await calistir('kaynak', async () => {
      try {
        const v = await cagir('/kaynak', { url: k.link });
        guncelle({ kaynak: `${k.ad}: ${v.baslik || k.baslik}\n${k.link}\n\n${v.metin}` });
      } catch (e) {
        guncelle({ kaynak: `${k.ad}: ${k.baslik}\n${k.link}\n\n${k.ozet}` });
        setKaynakUyari('Haberin tam metni çekilemedi, yalnız özeti kondu. Duyuruyu linkten kopyalayıp Kaynak alanına ekleyin.');
      }
    });
  };

  /* --- 2 · yaz --- */
  const yaz = () => calistir('yaz', async () => {
    const v = await cagir('/yaz', { hesap, konu: g.konu, aci: g.aci, not: g.not, kaynak: g.kaynak, tur: g.tur, uzunluk, stil });
    guncelle({
      metin: v.metin, kancalar: v.kancalar || [], etiketler: v.etiketler || [], notlar: v.notlar || [],
      hero: v.hero || '', alt: v.alt || '', sahneler: v.sahneler || [],
      istem: v.sahneler?.[0]?.istem || g.istem,
    });
  });

  const duzelt = (t) => calistir('duzelt', async () => {
    const v = await cagir('/duzelt', { hesap, metin: g.metin, talimat: t });
    guncelle({ metin: v.metin });
    setTalimat('');
  });

  // Kanca = ilk paragraf. Seçilen alternatif yalnız onu değiştirir.
  const kancaKoy = (k) => {
    const i = g.metin.indexOf('\n\n');
    guncelle({ metin: i > 0 ? `${k}${g.metin.slice(i)}` : `${k}\n\n${g.metin}` });
  };

  const tamMetin = () => [g.metin.trim(), (g.etiketler || []).join(' ')].filter(Boolean).join('\n\n');
  const kopyala = async () => {
    await navigator.clipboard.writeText(tamMetin());
    setKopyalandi(true);
    setTimeout(() => setKopyalandi(false), 1500);
  };

  /* --- 3 · görsel --- */
  const sahneUret = () => calistir('sahne', async () => {
    const v = await cagir('/sahne', { hesap, metin: g.metin, konu: g.konu, stil, yonlendirme });
    guncelle({ sahneler: v.sahneler || [], istem: v.sahneler?.[0]?.istem || g.istem });
  });

  const gorselUret = () => calistir('gorsel', async () => {
    const v = await cagir('/gorsel', { istem: g.istem, stil, enBoy });
    setG((o) => {
      const yeni = { url: v.url, dosya: v.dosya, enBoy: v.enBoy, stil, istem: o.istem };
      return { ...o, gorseller: [yeni, ...o.gorseller], secili: yeni };
    });
  });

  const kartCiz = () => calistir('kart', async () => {
    setKartUyari('');
    const v = await cagir('/kart', { dosya: g.secili.dosya, hero: g.hero, alt: g.alt, logo, enBoy: g.secili.enBoy, konuLogo, baslikYok });
    setKartUyari(v.uyari || '');
    setG((o) => ({ ...o, kartlar: [{ url: v.url, dosya: v.dosya, enBoy: o.secili.enBoy }, ...o.kartlar] }));
  });

  /* --- kaydet / yükle --- */
  const kaydet = (durum) => calistir('kaydet', async () => {
    const v = await cagir('/kaydet', {
      id: g.id, hesap, tur: g.tur, konu: g.konu, aci: g.aci, metin: g.metin, etiketler: g.etiketler,
      hero: g.hero, alt: g.alt, gorseller: g.gorseller, kartlar: g.kartlar, durum: durum || g.durum, oneriRef: g.oneriRef,
    });
    guncelle({ id: v.kayit.id, durum: v.kayit.durum });
    setKayitDurum(`Kaydedildi · ${tarihSaat(v.kayit.guncellendi)}`);
  });

  const ac = (k) => {
    setHesap(k.hesap || 'glowytics');
    setG({ ...BOS, ...k, secili: k.gorseller?.[0] || null, sahneler: [], kancalar: [], notlar: [] });
    const t = TURLER.find((x) => x.k === k.tur);
    if (t) { setStil(t.stil); setBaslikYok(!!t.baslikYok); }
    setFikirler([]);
    setKayitDurum(`Açıldı · son kayıt ${tarihSaat(k.guncellendi)}`);
    setSekme('yeni');
  };

  const sil = (id) => {
    if (!window.confirm('Bu taslak silinsin mi?')) return;
    calistir('gecmis', async () => {
      await cagir(`/kayit/${id}`, null, 'DELETE');
      setKayitlar((l) => l.filter((x) => x.id !== id));
      if (g.id === id) guncelle({ id: null });
    });
  };

  const yeni = () => { setG(BOS); setFikirler([]); setKayitDurum(''); setKartUyari(''); setKaynakUyari(''); };

  const karakter = g.metin.length;
  const gorunen = g.metin.slice(0, LINKEDIN_KESIM);

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 1600, margin: '0 auto', color: T.metin }}>
      <style>{`@keyframes lsDon{to{transform:rotate(360deg)}}
        .ls-izgara{display:grid;grid-template-columns:minmax(280px,340px) minmax(0,1fr) minmax(0,1.05fr);gap:16px;align-items:start}
        @media (max-width:1280px){.ls-izgara{grid-template-columns:minmax(0,1fr) minmax(0,1fr)}.ls-izgara>section:first-child{grid-column:1/-1}}
        @media (max-width:820px){.ls-izgara{grid-template-columns:minmax(0,1fr)}}
        .ls-tarz{display:grid;grid-template-columns:minmax(0,420px) minmax(0,1fr);gap:16px;align-items:start}
        @media (max-width:900px){.ls-tarz{grid-template-columns:minmax(0,1fr)}}
        .ls-fikir:hover{border-color:${T.mavi}!important}`}</style>

      {/* başlık */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>LinkedIn Stüdyosu</h1>
          <div style={{ fontSize: 13, color: T.metin3, marginTop: 2 }}>Konu bul, gönderiyi yaz, görselini Gemini ile üret.</div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginLeft: 16 }}>
          <Cip aktif={sekme === 'yeni'} onClick={() => setSekme('yeni')}>Gönderi</Cip>
          <Cip aktif={sekme === 'taslaklar'} onClick={() => setSekme('taslaklar')}>Taslaklar</Cip>
          <Cip aktif={sekme === 'tarz'} onClick={() => setSekme('tarz')}>Tarzımız</Cip>
          <Cip aktif={sekme === 'vitrin'} onClick={() => setSekme('vitrin')}>Özelliklerimiz</Cip>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: T.metin3 }}>Hesap</span>
          <Cip aktif={hesap === 'glowytics'} onClick={() => { setHesap('glowytics'); setLogo('glowytics'); }}>Glowytics</Cip>
          <Cip aktif={hesap === 'glowy'} onClick={() => { setHesap('glowy'); setLogo('glowy'); }}>Glowy</Cip>
        </div>
      </div>

      {sekme === 'tarz' ? (
        <Tarzimiz />
      ) : sekme === 'vitrin' ? (
        <Vitrin hesap={hesap} />
      ) : sekme === 'taslaklar' ? (
        <Taslaklar kayitlar={kayitlar} yukleniyor={mesgul.gecmis} hata={hata.gecmis} ac={ac} sil={sil} yenile={gecmisiYukle} />
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <Dugme ikincil onClick={yeni}><Plus size={14} /> Yeni gönderi</Dugme>
            <select value={g.durum} onChange={(e) => guncelle({ durum: e.target.value })} style={{ ...alan, width: 'auto', padding: '7px 10px' }}>
              {DURUMLAR.map(([k, a]) => <option key={k} value={k}>{a}</option>)}
            </select>
            <Dugme onClick={() => kaydet()} disabled={mesgul.kaydet || !(g.konu || g.metin)}><Save size={14} /> {g.id ? 'Kaydet' : 'Taslak olarak kaydet'}</Dugme>
            <span style={{ fontSize: 12, color: T.metin3 }}>{kayitDurum}</span>
            {hata.kaydet && <span style={{ fontSize: 12, color: T.kirmizi }}>{hata.kaydet}</span>}
          </div>

          <Oneriler hesap={hesap} secili={g.oneriRef} onYaz={onerideYaz} />

          <div className="ls-izgara">
            {/* 1 · KONU */}
            <Sutun no={1} ikon={Lightbulb} baslik="Konu">
              <Etiket>Fikir lazımsa</Etiket>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={tema} onChange={(e) => setTema(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fikirUret()}
                  placeholder="Tema (boş bırakılabilir): PMax, AI görünürlüğü…" style={alan} />
                <Dugme onClick={fikirUret} disabled={mesgul.fikir} title="Konu fikirleri üret"><Sparkles size={14} /></Dugme>
              </div>
              {mesgul.fikir && <Yukleniyor metin="Fikirler düşünülüyor…" />}
              <Hata mesaj={hata.fikir} />
              {fikirler.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10, maxHeight: 420, overflowY: 'auto', paddingRight: 2 }}>
                  {fikirler.map((f, i) => {
                    const secili = g.konu === f.konu;
                    return (
                      <button key={i} type="button" className="ls-fikir"
                        onClick={() => { guncelle({ konu: f.konu, aci: [f.aci, f.kanca ? `Kanca önerisi: ${f.kanca}` : ''].filter(Boolean).join('\n') }); if (f.tur) turSec(f.tur); }}
                        style={{ textAlign: 'left', border: `1px solid ${secili ? T.mavi : T.cizgi}`, background: secili ? T.maviAcik : T.kart, borderRadius: 10, padding: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.metin }}>{f.konu}</div>
                        <div style={{ fontSize: 12, color: T.metin2, marginTop: 4, lineHeight: 1.45 }}>{f.aci}</div>
                        <div style={{ fontSize: 12, color: T.metin3, marginTop: 6, fontStyle: 'italic', lineHeight: 1.45 }}>“{f.kanca}”</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                          {f.tur && <span style={{ fontSize: 11, color: '#fff', background: T.mavi, borderRadius: 999, padding: '2px 8px' }}>{TUR_ADI[f.tur] || f.tur}</span>}
                          <span style={{ fontSize: 11, color: T.mavi, background: T.maviAcik, borderRadius: 999, padding: '2px 8px' }}>{f.format}</span>
                        </div>
                        <div style={{ fontSize: 11, color: T.metin3, marginTop: 6 }}>{f.neden}</div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div style={{ borderTop: `1px solid ${T.cizgi2}`, margin: '16px 0 0' }} />
              <Etiket>Konu</Etiket>
              <input value={g.konu} onChange={(e) => guncelle({ konu: e.target.value })} placeholder="Gönderi neyi anlatıyor?" style={alan} />
              <Etiket>Bakış açısı</Etiket>
              <textarea rows={3} value={g.aci} onChange={(e) => guncelle({ aci: e.target.value })} placeholder="Bu konuyu farklı kılan ne?" style={alan} />
              <Etiket>Olmazsa olmazlar</Etiket>
              <textarea rows={2} value={g.not} onChange={(e) => guncelle({ not: e.target.value })} placeholder="Değinilmesi gereken nokta, yeni özellik adı, link…" style={alan} />
              <button type="button" onClick={() => setKaynakAcik((x) => !x)} style={{ background: 'none', border: 'none', color: T.mavi, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, marginTop: 10 }}>
                {kaynakAcik ? '− Kaynak metni gizle' : '+ Kaynak metin ekle (duyuru, doküman)'}
              </button>
              {kaynakAcik && (
                <textarea rows={5} value={g.kaynak} onChange={(e) => guncelle({ kaynak: e.target.value })}
                  placeholder="Rakam ve iddialar yalnız buradan alınır. Kaynak yoksa model rakam yerine [rakam] bırakır." style={{ ...alan, marginTop: 6 }} />
              )}
              {mesgul.kaynak && <Yukleniyor metin="Haberin metni çekiliyor…" />}
              {kaynakUyari && <div style={{ fontSize: 12, color: T.turuncu, marginTop: 6, lineHeight: 1.45 }}>{kaynakUyari}</div>}
              <Etiket>Gönderi türü</Etiket>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {TURLER.map((t) => <Cip key={t.k} kucuk aktif={g.tur === t.k} onClick={() => turSec(t.k)}>{t.ad}</Cip>)}
              </div>
              <div style={{ fontSize: 12, color: T.metin3, marginTop: 6, lineHeight: 1.45 }}>Görsel: {aktifTur.ipucu}</div>
              <Etiket>Uzunluk</Etiket>
              <div style={{ display: 'flex', gap: 6 }}>
                {UZUNLUKLAR.map(([k, a]) => <Cip key={k} kucuk aktif={uzunluk === k} onClick={() => setUzunluk(k)}>{a}</Cip>)}
              </div>
              <div style={{ marginTop: 16 }}>
                <Dugme onClick={yaz} disabled={mesgul.yaz || !g.konu.trim()}><PenLine size={14} /> {g.metin ? 'Yeniden yaz' : 'Gönderiyi yaz'}</Dugme>
              </div>
              {g.tur === 'haber' && !g.kaynak.trim() && (
                <div style={{ fontSize: 12, color: T.turuncu, marginTop: 8, lineHeight: 1.45 }}>
                  Haber analizinde duyuru metnini kaynak alanına yapıştır. Yoksa platform iddiaları yer tutucu olarak gelir.
                </div>
              )}
              {mesgul.yaz && <Yukleniyor metin="Metin, başlık ve görsel sahneleri yazılıyor (20-60 sn)…" />}
              <Hata mesaj={hata.yaz} />
            </Sutun>

            {/* 2 · METİN */}
            <Sutun no={2} ikon={PenLine} baslik="Gönderi metni"
              sag={g.metin && <Dugme ikincil onClick={kopyala}>{kopyalandi ? <Check size={14} /> : <Copy size={14} />} {kopyalandi ? 'Kopyalandı' : 'Kopyala'}</Dugme>}>
              <textarea rows={18} value={g.metin} onChange={(e) => guncelle({ metin: e.target.value })}
                placeholder="Soldan bir konu seçip “Gönderiyi yaz”a bas ya da kendi metnini buraya yapıştır." style={{ ...alan, lineHeight: 1.55, fontSize: 14 }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.metin3, marginTop: 6 }}>
                <span>{karakter.toLocaleString('tr-TR')} karakter</span>
                <span style={{ color: karakter > 3000 ? T.kirmizi : T.metin3 }}>LinkedIn sınırı 3.000</span>
              </div>

              {g.metin && (
                <>
                  <Etiket>Akışta görünen kısım (ilk {LINKEDIN_KESIM} karakter)</Etiket>
                  <div style={{ background: T.yuzey, border: `1px solid ${T.cizgi2}`, borderRadius: 8, padding: 10, fontSize: 13, color: T.metin2, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {gorunen}{g.metin.length > LINKEDIN_KESIM && <span style={{ color: T.metin3 }}>… daha fazlasını gör</span>}
                  </div>
                </>
              )}

              {g.kancalar.length > 0 && (
                <>
                  <Etiket>Alternatif girişler · tıkla, ilk paragrafın yerine geçsin</Etiket>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {g.kancalar.map((k, i) => (
                      <button key={i} type="button" className="ls-fikir" onClick={() => kancaKoy(k)}
                        style={{ textAlign: 'left', border: `1px solid ${T.cizgi}`, background: T.kart, borderRadius: 8, padding: '8px 10px', fontSize: 13, color: T.metin2, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.45 }}>{k}</button>
                    ))}
                  </div>
                </>
              )}

              {g.metin && (
                <>
                  <Etiket>Düzelt</Etiket>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {HIZLI_DUZELTME.map((t) => <Cip key={t} kucuk onClick={() => !mesgul.duzelt && duzelt(t)}>{t}</Cip>)}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <input value={talimat} onChange={(e) => setTalimat(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && talimat.trim() && duzelt(talimat)}
                      placeholder="Kendi talimatın: “3. paragrafı GA4 örneğiyle değiştir”" style={alan} />
                    <Dugme onClick={() => duzelt(talimat)} disabled={mesgul.duzelt || !talimat.trim()}><Wand2 size={14} /></Dugme>
                  </div>
                  {mesgul.duzelt && <Yukleniyor metin="Düzenleniyor…" />}
                  <Hata mesaj={hata.duzelt} />
                </>
              )}

              {(g.etiketler.length > 0 || g.metin) && (
                <>
                  <Etiket>Hashtag'ler</Etiket>
                  <input value={g.etiketler.join(' ')} onChange={(e) => guncelle({ etiketler: e.target.value.split(/\s+/).filter(Boolean) })} placeholder="#pazarlama #googleads" style={alan} />
                </>
              )}

              {g.notlar.length > 0 && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 12px', marginTop: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.turuncu, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={13} /> Yayından önce kontrol et</div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: T.metin2, lineHeight: 1.5 }}>
                    {g.notlar.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              )}
            </Sutun>

            {/* 3 · GÖRSEL */}
            <Sutun no={3} ikon={ImageIcon} baslik="Görsel">
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div>
                  <Etiket>Stil</Etiket>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {STILLER.map(([k, a]) => <Cip key={k} kucuk aktif={stil === k} onClick={() => setStil(k)}>{a}</Cip>)}
                  </div>
                </div>
                <div>
                  <Etiket>Oran</Etiket>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {ORANLAR.map((o) => <Cip key={o} kucuk aktif={enBoy === o} onClick={() => setEnBoy(o)}>{o}</Cip>)}
                  </div>
                </div>
              </div>

              <Etiket>Görsel fikirleri</Etiket>
              <div style={{ display: 'flex', gap: 6 }}>
                <input value={yonlendirme} onChange={(e) => setYonlendirme(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (g.metin || g.konu) && sahneUret()}
                  placeholder="İstersen yön ver: “mıknatıs metaforu”, “ofis masası”…" style={alan} />
                <Dugme ikincil onClick={sahneUret} disabled={mesgul.sahne || !(g.metin || g.konu)} title="Yeni sahne fikirleri"><Sparkles size={14} /></Dugme>
              </div>
              {mesgul.sahne && <Yukleniyor metin="Sahneler düşünülüyor…" />}
              <Hata mesaj={hata.sahne} />
              {g.sahneler.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                  {g.sahneler.map((s, i) => (
                    <button key={i} type="button" className="ls-fikir" onClick={() => guncelle({ istem: s.istem })}
                      style={{ textAlign: 'left', border: `1px solid ${g.istem === s.istem ? T.mavi : T.cizgi}`, background: g.istem === s.istem ? T.maviAcik : T.kart, borderRadius: 8, padding: '8px 10px', fontSize: 13, color: T.metin2, cursor: 'pointer', fontFamily: 'inherit', lineHeight: 1.45 }}>
                      {s.ozet}
                    </button>
                  ))}
                </div>
              )}

              <Etiket>Gemini istemi (İngilizce, düzenlenebilir{stil === 'glowy3d' ? '; ışık, renk, malzeme kuralları otomatik eklenir' : ''})</Etiket>
              <textarea rows={5} value={g.istem} onChange={(e) => guncelle({ istem: e.target.value })}
                placeholder={stil === 'glowy3d' ? 'Scene: one large chunky 3D violet … standing upright on a matte surface…' : 'Describe the scene in English…'} style={{ ...alan, fontSize: 12, lineHeight: 1.5 }} />
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Dugme onClick={gorselUret} disabled={mesgul.gorsel || !g.istem.trim()}><ImageIcon size={14} /> {g.gorseller.length ? 'Bir tane daha üret' : 'Görseli üret'}</Dugme>
                <span style={{ fontSize: 11, color: T.metin3 }}>Her üretim ~30-90 sn, günlük kota ~250 görsel.</span>
              </div>
              {mesgul.gorsel && <Yukleniyor metin="Gemini çiziyor…" />}
              <Hata mesaj={hata.gorsel} />

              {g.gorseller.length > 0 && (
                <>
                  <Etiket>Üretilenler · karta koymak için seç</Etiket>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
                    {g.gorseller.map((x) => (
                      <button key={x.dosya} type="button" onClick={() => guncelle({ secili: x })}
                        style={{ padding: 0, border: `2px solid ${g.secili?.dosya === x.dosya ? T.mavi : 'transparent'}`, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', background: T.cizgi2, aspectRatio: x.enBoy.replace(':', '/') }}>
                        <img src={x.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </button>
                    ))}
                  </div>
                </>
              )}

              {g.secili && (
                <div style={{ borderTop: `1px solid ${T.cizgi2}`, marginTop: 16, paddingTop: 4 }}>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <img src={g.secili.url} alt="" style={{ width: 180, borderRadius: 8, border: `1px solid ${T.cizgi}` }} />
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.metin2, marginTop: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={baslikYok} onChange={(e) => setBaslikYok(e.target.checked)} />
                        Başlıksız: yalnız sol üstte logo şeridi (etkinlik görseli gibi)
                      </label>
                      {!baslikYok && (
                        <>
                          <Etiket>Kart başlığı (satırları Enter ile ayır)</Etiket>
                          <textarea rows={3} value={g.hero} onChange={(e) => guncelle({ hero: e.target.value })} placeholder={'Kısa,\nüç satırlık\nbaşlık.'} style={{ ...alan, fontWeight: 600 }} />
                          <Etiket>Alt satır (isteğe bağlı)</Etiket>
                          <input value={g.alt} onChange={(e) => guncelle({ alt: e.target.value })} style={alan} />
                        </>
                      )}
                      <Etiket>{baslikYok ? 'Yanına gelecek logo (etkinlik, partner)' : 'Konu logosu, başlığın üstünde (YouTube, Google…)'}</Etiket>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: `1px solid ${T.cizgi}`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: T.metin2, cursor: 'pointer' }}>
                          <Upload size={13} /> Resmî logo yükle
                          <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" style={{ display: 'none' }}
                            onChange={async (e) => {
                              const f = e.target.files?.[0];
                              e.target.value = '';
                              if (!f) return;
                              try { setKonuLogo(await dosyadanDataUrl(f)); setHata((h) => ({ ...h, kart: '' })); } catch (err) { setHata((h) => ({ ...h, kart: err.message })); }
                            }} />
                        </label>
                        {konuLogo && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: T.yuzey, border: `1px solid ${T.cizgi2}`, borderRadius: 8, padding: '4px 8px' }}>
                            <img src={konuLogo} alt="" style={{ height: 20 }} />
                            <button type="button" onClick={() => setKonuLogo('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}><X size={13} color={T.metin3} /></button>
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: T.metin3, marginTop: 4 }}>Logoyu Gemini'ye çizdirmiyoruz, harfleri bozuyor; resmî dosyayı yükle.</div>
                      <Etiket>Logo</Etiket>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Cip kucuk aktif={logo === 'glowytics'} onClick={() => setLogo('glowytics')}>Glowytics</Cip>
                        <Cip kucuk aktif={logo === 'glowy'} onClick={() => setLogo('glowy')}>Glowy</Cip>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                        <Dugme onClick={kartCiz} disabled={mesgul.kart || (!baslikYok && !g.hero.trim())}><LayoutTemplate size={14} /> Kartı oluştur</Dugme>
                        <Dugme ikincil onClick={() => indir(g.secili.url, `gorsel-${g.secili.dosya}`)}><Download size={14} /> Yazısız indir</Dugme>
                      </div>
                      {mesgul.kart && <Yukleniyor metin="Kart çiziliyor…" />}
                      <Hata mesaj={hata.kart} />
                      {kartUyari && <div style={{ fontSize: 12, color: T.turuncu, marginTop: 8 }}>{kartUyari}</div>}
                    </div>
                  </div>
                </div>
              )}

              {g.kartlar.length > 0 && (
                <>
                  <Etiket>Kartlar</Etiket>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                    {g.kartlar.map((k) => (
                      <div key={k.dosya}>
                        <a href={k.url} target="_blank" rel="noopener noreferrer">
                          <img src={k.url} alt="" style={{ width: '100%', borderRadius: 8, border: `1px solid ${T.cizgi}`, display: 'block' }} />
                        </a>
                        <button type="button" onClick={() => indir(k.url, `linkedin-${k.dosya}`)}
                          style={{ marginTop: 6, background: 'none', border: 'none', color: T.mavi, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Download size={12} /> İndir
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Sutun>
          </div>
        </>
      )}
    </div>
  );
}

/* ───────────────────────────── taslaklar ───────────────────────────── */

function Taslaklar({ kayitlar, yukleniyor, hata, ac, sil, yenile }) {
  const durumAdi = Object.fromEntries(DURUMLAR);
  const durumRenk = { taslak: T.metin3, hazir: T.turuncu, yayinlandi: T.yesil };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: T.metin3 }}>{kayitlar.length} kayıt · ekipteki herkesin taslakları</span>
        <div style={{ marginLeft: 'auto' }}><Dugme ikincil onClick={yenile} disabled={yukleniyor}><RefreshCw size={14} /> Yenile</Dugme></div>
      </div>
      <Hata mesaj={hata} />
      {!yukleniyor && kayitlar.length === 0 && (
        <div style={{ background: T.kart, border: `1px dashed ${T.cizgi}`, borderRadius: 12, padding: 40, textAlign: 'center', color: T.metin3, fontSize: 14 }}>
          Henüz kayıtlı gönderi yok.
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
        {kayitlar.map((k) => {
          const kapak = k.kartlar?.[0]?.url || k.gorseller?.[0]?.url;
          return (
            <div key={k.id} style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ aspectRatio: '4/3', background: T.yuzey, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {kapak ? <img src={kapak} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <PenLine size={28} color={T.cizgi} />}
              </div>
              <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: durumRenk[k.durum] }}>● {durumAdi[k.durum] || k.durum}</span>
                  <span style={{ fontSize: 11, color: T.metin3 }}>· {k.hesap === 'glowy' ? 'Glowy' : 'Glowytics'}{k.tur ? ` · ${TUR_ADI[k.tur] || k.tur}` : ''}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.metin, lineHeight: 1.35 }}>{k.konu || 'Konusuz taslak'}</div>
                <div style={{ fontSize: 12, color: T.metin3, marginTop: 6, lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{k.metin}</div>
                <div style={{ fontSize: 11, color: T.metin3, marginTop: 'auto', paddingTop: 10 }}>{k.guncelleyen} · {tarihSaat(k.guncellendi)}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <Dugme onClick={() => ac(k)}><FolderOpen size={14} /> Aç</Dugme>
                  <Dugme ikincil tehlike onClick={() => sil(k.id)}><Trash2 size={14} /></Dugme>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ───────────────────────────── ne paylaşalım ───────────────────────────── */

const gunSaat = (iso) => new Date(iso).toLocaleString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const gunAy = (iso) => new Date(iso).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });

// Gündem (sektör haberleri), Özelliklerimiz ve Takvim'den haftalık öneriler. Sayfa açılınca
// son üretilen gelir; hiç yoksa ya da 12 saatten eskiyse kendiliğinden yenilenir.
function Oneriler({ hesap, secili, onYaz }) {
  const [veri, setVeri] = useState(null);
  const [filtre, setFiltre] = useState('hepsi');
  const [acik, setAcik] = useState(true);
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState('');

  const uret = async () => {
    setMesgul(true); setHata('');
    try { setVeri(await cagir('/oneriler', { hesap })); } catch (e) { setHata(e.message); }
    setMesgul(false);
  };

  useEffect(() => {
    let iptal = false;
    setVeri(null); setHata('');
    cagir(`/oneriler?hesap=${hesap}`, null, 'GET')
      .then((v) => { if (iptal) return; if (v.oneriler && !v.eski) setVeri(v); else { if (v.oneriler) setVeri(v); uret(); } })
      .catch((e) => !iptal && setHata(e.message));
    return () => { iptal = true; };
  }, [hesap]); // eslint-disable-line react-hooks/exhaustive-deps

  const liste = (veri?.oneriler || []).filter((o) => filtre === 'hepsi' || o.kaynak.tip === filtre);
  const sayi = (t) => (veri?.oneriler || []).filter((o) => o.kaynak.tip === t).length;
  const refAl = (o) => (o.kaynak.tip === 'haber' ? `haber:${o.kaynak.link}` : o.ref);

  return (
    <section style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Compass size={18} color={T.mavi} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Bu hafta ne paylaşalım?</div>
          <div style={{ fontSize: 12, color: T.metin3, marginTop: 1 }}>
            {veri?.olusturuldu
              ? `Sektör haberleri, özelliklerimiz ve yaklaşan tarihlerden · ${veri.haberSayisi || 0} haber tarandı · ${gunSaat(veri.olusturuldu)}`
              : 'Sektör haberleri, özelliklerimiz ve yaklaşan tarihlerden öneriler'}
          </div>
        </div>
        {acik && veri?.oneriler?.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexWrap: 'wrap' }}>
            <Cip kucuk aktif={filtre === 'hepsi'} onClick={() => setFiltre('hepsi')}>Tümü {veri.oneriler.length}</Cip>
            {Object.entries(ONERI_KAYNAK).map(([k, v]) => (
              <Cip key={k} kucuk aktif={filtre === k} onClick={() => setFiltre(k)}>{v.ad} {sayi(k)}</Cip>
            ))}
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <Dugme ikincil onClick={uret} disabled={mesgul} title="Haberleri yeniden tara, yeni öneri üret"><RefreshCw size={14} /> Yenile</Dugme>
          <Dugme ikincil onClick={() => setAcik((x) => !x)} title={acik ? 'Gizle' : 'Göster'}>{acik ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</Dugme>
        </div>
      </div>

      {acik && (
        <>
          {mesgul && <Yukleniyor metin="Haberler taranıyor, öneriler hazırlanıyor (30-60 sn)…" />}
          <Hata mesaj={hata} />
          {liste.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10, marginTop: 14, opacity: mesgul ? 0.5 : 1 }}>
              {liste.map((o, i) => {
                const k = ONERI_KAYNAK[o.kaynak.tip];
                const Ikon = k.ikon;
                const p = ONCELIK[o.oncelik] || ONCELIK.planla;
                const sec = secili && secili === refAl(o);
                return (
                  <div key={`${o.ref}-${i}`} style={{ border: `1px solid ${sec ? T.mavi : T.cizgi}`, background: sec ? T.maviAcik : T.kart, borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: k.renk, background: k.zemin, borderRadius: 999, padding: '2px 8px' }}><Ikon size={11} /> {k.ad}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: p.renk, background: p.zemin, borderRadius: 999, padding: '2px 8px' }}>{p.ad}</span>
                      {o.ne_zaman && o.ne_zaman !== p.ad && <span style={{ fontSize: 11, color: T.metin3 }}>{o.ne_zaman}</span>}
                      <span style={{ fontSize: 11, color: T.metin3, marginLeft: 'auto' }}>{TUR_ADI[o.tur] || o.tur}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginTop: 8, lineHeight: 1.35 }}>{o.konu}</div>
                    <div style={{ fontSize: 12, color: T.metin3, marginTop: 6, fontStyle: 'italic', lineHeight: 1.45 }}>“{o.kanca}”</div>
                    <div style={{ fontSize: 12, color: T.metin2, marginTop: 6, lineHeight: 1.45 }}>{o.neden}</div>
                    <div style={{ fontSize: 11, color: T.metin3, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.cizgi2}`, lineHeight: 1.45, minWidth: 0 }}>
                      {o.kaynak.tip === 'haber' ? (
                        <a href={o.kaynak.link} target="_blank" rel="noopener noreferrer" style={{ color: T.metin2, textDecoration: 'none', display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                          <ExternalLink size={11} style={{ flexShrink: 0, marginTop: 2 }} />
                          <span><b style={{ fontWeight: 600 }}>{o.kaynak.ad}</b>{o.kaynak.resmi ? ' (resmî)' : ''} · {gunAy(o.kaynak.tarih)} · {o.kaynak.baslik}</span>
                        </a>
                      ) : o.kaynak.tip === 'takvim' ? (
                        <span><b style={{ fontWeight: 600 }}>{o.kaynak.baslik}</b> · {gunAy(o.kaynak.tarih)}{o.kaynak.kalan > 0 ? ` · ${o.kaynak.kalan} gün kaldı` : ' · sürüyor'}</span>
                      ) : (
                        <span><b style={{ fontWeight: 600 }}>{o.kaynak.baslik}</b> · {o.kaynak.sonPaylasim ? `en son ${gunAy(o.kaynak.sonPaylasim)} işlendi` : 'stüdyoda henüz işlenmedi'}</span>
                      )}
                    </div>
                    <div style={{ marginTop: 'auto', paddingTop: 10 }}>
                      <Dugme ikincil={!sec} onClick={() => onYaz(o)}><PenLine size={14} /> {sec ? 'Seçildi' : 'Bunu yaz'}</Dugme>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}

/* ───────────────────────────── özelliklerimiz ───────────────────────────── */

// Önerilerin "Özelliklerimiz" ayağı bu listeden. "İç kullanımda" olanlar önerilmez:
// duyurulmamış bir özellik LinkedIn'e çıkmasın.
function Vitrin({ hesap }) {
  const [ozellikler, setOzellikler] = useState([]);
  const [duzen, setDuzen] = useState({ id: null, ad: '', ozet: '', durum: 'yayinda' });
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState('');

  const calis = async (fn) => {
    setMesgul(true); setHata('');
    try { setOzellikler((await fn()).ozellikler || []); } catch (e) { setHata(e.message); }
    setMesgul(false);
  };
  useEffect(() => { calis(() => cagir('/vitrin', null, 'GET')); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kaydet = () => calis(async () => {
    const v = await cagir('/vitrin/ozellik', { ...duzen, hesap });
    setDuzen({ id: null, ad: '', ozet: '', durum: 'yayinda' });
    return v;
  });
  const durumDegis = (o) => calis(() => cagir('/vitrin/ozellik', { ...o, durum: o.durum === 'ic' ? 'yayinda' : 'ic' }));
  const sil = (o) => { if (window.confirm(`"${o.ad}" listeden çıksın mı?`)) calis(() => cagir(`/vitrin/ozellik/${o.id}`, null, 'DELETE')); };

  const liste = ozellikler.filter((o) => o.hesap === hesap);

  return (
    <div className="ls-tarz">
      <section style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Package size={16} color={T.metin2} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>{duzen.id ? 'Özelliği düzenle' : 'Özellik ekle'}</div>
        </div>
        <div style={{ fontSize: 12, color: T.metin3, lineHeight: 1.5 }}>
          "Bu hafta ne paylaşalım" önerileri buradaki listeden gelir; uzun süredir paylaşılmayan öne alınır.
          Yeni bir özellik yayına girince buraya ekle. Henüz duyurulmayanı "İç kullanımda" bırak, önerilere girmez.
        </div>
        <Etiket>Özellik / hizmet adı</Etiket>
        <input value={duzen.ad} onChange={(e) => setDuzen((d) => ({ ...d, ad: e.target.value }))} placeholder="GlowyAI ile sunum üretme" style={alan} />
        <Etiket>Ne yapıyor (1-2 cümle, okurun sorunu üzerinden)</Etiket>
        <textarea rows={3} value={duzen.ozet} onChange={(e) => setDuzen((d) => ({ ...d, ozet: e.target.value }))} style={alan} />
        <Etiket>Durum</Etiket>
        <div style={{ display: 'flex', gap: 6 }}>
          <Cip kucuk aktif={duzen.durum === 'yayinda'} onClick={() => setDuzen((d) => ({ ...d, durum: 'yayinda' }))}>Yayında, paylaşılabilir</Cip>
          <Cip kucuk aktif={duzen.durum === 'ic'} onClick={() => setDuzen((d) => ({ ...d, durum: 'ic' }))}>İç kullanımda</Cip>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 6 }}>
          <Dugme onClick={kaydet} disabled={mesgul || !duzen.ad.trim() || duzen.ozet.trim().length < 10}><Save size={14} /> {duzen.id ? 'Kaydet' : 'Ekle'}</Dugme>
          {duzen.id && <Dugme ikincil onClick={() => setDuzen({ id: null, ad: '', ozet: '', durum: 'yayinda' })}>Vazgeç</Dugme>}
        </div>
        <Hata mesaj={hata} />
      </section>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: T.metin3 }}>{hesap === 'glowy' ? 'Glowy' : 'Glowytics'} hesabının listesi · {liste.filter((o) => o.durum !== 'ic').length} yayında</div>
        {mesgul && !liste.length && <Yukleniyor metin="Yükleniyor…" />}
        {liste.map((o) => (
          <div key={o.id} style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 10, padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'flex-start', opacity: o.durum === 'ic' ? 0.65 : 1 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{o.ad}</span>
                <span style={{ fontSize: 11, fontWeight: 600, borderRadius: 999, padding: '2px 8px', color: o.durum === 'ic' ? T.metin2 : T.yesil, background: o.durum === 'ic' ? T.cizgi2 : '#ECFDF5' }}>{o.durum === 'ic' ? 'İç kullanımda' : 'Yayında'}</span>
              </div>
              <div style={{ fontSize: 12, color: T.metin2, marginTop: 4, lineHeight: 1.5 }}>{o.ozet}</div>
            </div>
            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
              <button type="button" onClick={() => durumDegis(o)} disabled={mesgul} title={o.durum === 'ic' ? 'Yayında yap' : 'İç kullanıma al'} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, fontSize: 11, color: T.mavi, fontWeight: 600 }}>{o.durum === 'ic' ? 'Yayına al' : 'Gizle'}</button>
              <button type="button" onClick={() => setDuzen({ id: o.id, ad: o.ad, ozet: o.ozet, durum: o.durum })} title="Düzenle" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}><PenLine size={14} color={T.metin3} /></button>
              <button type="button" onClick={() => sil(o)} title="Sil" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}><Trash2 size={14} color={T.metin3} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────────── tarzımız ───────────────────────────── */

// Ekibin beğendiği gönderiler. Metin yazılırken aynı türden ikisi modele örnek gösteriliyor.
function Tarzimiz() {
  const [ornekler, setOrnekler] = useState([]);
  const [yeniMetin, setYeniMetin] = useState('');
  const [yeniTur, setYeniTur] = useState('haber');
  const [yeniNot, setYeniNot] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState('');

  const yukle = async () => {
    setMesgul(true); setHata('');
    try { setOrnekler((await cagir('/ornekler', null, 'GET')).ornekler || []); } catch (e) { setHata(e.message); }
    setMesgul(false);
  };
  useEffect(() => { yukle(); }, []);

  const ekle = async () => {
    setMesgul(true); setHata('');
    try {
      const v = await cagir('/ornekler', { metin: yeniMetin, tur: yeniTur, not: yeniNot });
      setOrnekler((l) => [v.ornek, ...l]);
      setYeniMetin(''); setYeniNot('');
    } catch (e) { setHata(e.message); }
    setMesgul(false);
  };

  const sil = async (id) => {
    if (!window.confirm('Bu örnek silinsin mi?')) return;
    try { await cagir(`/ornekler/${id}`, null, 'DELETE'); setOrnekler((l) => l.filter((o) => o.id !== id)); } catch (e) { setHata(e.message); }
  };

  return (
    <div className="ls-tarz">
      <section style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <BookOpen size={16} color={T.metin2} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>Örnek gönderi ekle</div>
        </div>
        <div style={{ fontSize: 12, color: T.metin3, lineHeight: 1.5 }}>
          Beğendiğimiz, tarzımızı yansıtan gönderileri buraya ekle. Yeni gönderi yazılırken aynı türden en fazla iki örnek modele gösteriliyor; cümleler değil, üslup alınıyor.
        </div>
        <Etiket>Tür</Etiket>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TURLER.map((t) => <Cip key={t.k} kucuk aktif={yeniTur === t.k} onClick={() => setYeniTur(t.k)}>{t.ad}</Cip>)}
        </div>
        <Etiket>Gönderi metni</Etiket>
        <textarea rows={12} value={yeniMetin} onChange={(e) => setYeniMetin(e.target.value)} placeholder="LinkedIn'den olduğu gibi yapıştır." style={{ ...alan, lineHeight: 1.5 }} />
        <Etiket>Kısa not (isteğe bağlı)</Etiket>
        <input value={yeniNot} onChange={(e) => setYeniNot(e.target.value)} placeholder="Neyi iyi: girişi, liste düzeni…" style={alan} />
        <div style={{ marginTop: 12 }}>
          <Dugme onClick={ekle} disabled={mesgul || yeniMetin.trim().length < 40}><Plus size={14} /> Örneklere ekle</Dugme>
        </div>
        <Hata mesaj={hata} />
      </section>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
        {mesgul && !ornekler.length && <Yukleniyor metin="Örnekler yükleniyor…" />}
        {ornekler.map((o) => (
          <div key={o.id} style={{ background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 11, color: '#fff', background: T.mavi, borderRadius: 999, padding: '2px 8px' }}>{TUR_ADI[o.tur] || o.tur}</span>
              {o.not && <span style={{ fontSize: 12, color: T.metin3 }}>{o.not}</span>}
              <button type="button" onClick={() => sil(o.id)} title="Sil" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}><Trash2 size={14} color={T.metin3} /></button>
            </div>
            <div style={{ fontSize: 13, color: T.metin2, whiteSpace: 'pre-wrap', lineHeight: 1.55, maxHeight: 260, overflowY: 'auto' }}>{o.metin}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
